// Role-based access for the coach app.
//
// Admins can do everything. Support staff are external and deny-by-default:
// a route or feature is only available to them when listed here. New pages
// and features stay admin-only until they are added explicitly.
//
// Exception: wearable / third-party integration features (Garmin and every
// future integration) are support-accessible by default. Key them under
// `integrations.*` or `wearables.*`. Their tables still need a support read
// policy: SELECT app.add_support_read_policy('public.<table>') in the migration.
//
// Support sees everything about an assigned user's activity except money:
// subscriptions, billing, purchases, access type, trials and B2B activation.
//
// This is UI gating only. The database enforces the same boundary through
// app.is_staff() / app.can_access_user() in RPCs and RLS policies.
//
// Admin-only features referenced in the UI (never add to SUPPORT_FEATURES):
// users.billing, users.manage, exercises.edit, exercises.engagement,
// exercises.videoPrivacy, sessions.delete, sessions.editCatalog, email.broadcast, email.templates.manage.

export const ROLES = {
  ADMIN: 'admin',
  SUPPORT: 'support',
};

export const STAFF_ROLES = [ROLES.ADMIN, ROLES.SUPPORT];

const SUPPORT_ROUTE_PREFIXES = [
  '/live-studio',
  '/exercises',
  '/exercise/',
  '/bookmarks',
  '/sessions',
  '/session-builder',
  '/user-activity',
  '/email-studio',
];

export const SUPPORT_HOME = '/live-studio';

const SUPPORT_FEATURES = new Set([
  'notes.reply',
  'notes.triage',
  'notes.aiAssist',
  'exercises.read',
  'exercises.thumbnail',
  'exercises.videoUpload',
  'sessions.read',
  'sessions.build',
  'sessions.assign',
  'users.read',
  'users.email',
  'users.aiChats',
  'users.alerts',
  'email.compose',
]);

const SUPPORT_FEATURE_PREFIXES = ['integrations.', 'wearables.'];

export const isStaffRole = (role) => STAFF_ROLES.includes(role);

export const canAccessRoute = (role, pathname) => {
  if (role === ROLES.ADMIN) return true;
  if (role !== ROLES.SUPPORT) return false;
  return SUPPORT_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`));
};

export const can = (role, feature) => {
  if (role === ROLES.ADMIN) return true;
  if (role === ROLES.SUPPORT) {
    return SUPPORT_FEATURES.has(feature) || SUPPORT_FEATURE_PREFIXES.some((prefix) => feature.startsWith(prefix));
  }
  return false;
};
